import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, MessageSquare, Mail, FileText, Star, TrendingUp, ArrowRight, MapPin, Package } from "lucide-react";
import { adminApi } from "../../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    adminApi.get("/inquiries").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-12" data-testid="admin-dashboard">
      <div>
        <div className="eyebrow mb-3">Overview</div>
        <h1 className="font-serif text-4xl md:text-6xl leading-tight tracking-tight">Atelier dashboard.</h1>
        <p className="mt-4 text-foreground/65 max-w-2xl">The state of your guests, your reservations, and your atelier — at a glance.</p>
      </div>

      {stats && (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {[
              { l: "Total Inquiries", v: stats.total_inquiries, icon: MessageSquare },
              { l: "New (Untouched)", v: stats.new_inquiries, icon: TrendingUp, accent: true },
              { l: "Last 30 days", v: stats.inquiries_last_30_days, icon: Users },
              { l: "Newsletter Subscribers", v: stats.subscribers, icon: Mail },
            ].map((s) => (
              <div key={s.l} className="bg-background p-6 lg:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="eyebrow text-foreground/55">{s.l}</div>
                  <s.icon size={16} strokeWidth={1.25} className={s.accent ? "text-accent" : "text-foreground/40"} />
                </div>
                <div className={`font-serif text-4xl md:text-5xl ${s.accent ? "text-accent" : ""}`}>{s.v}</div>
              </div>
            ))}
          </div>

          {/* Pipeline + content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Pipeline */}
            <div className="lg:col-span-2 border border-border p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-2xl">Inquiry pipeline</h3>
                <Link to="/admin/inquiries" className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 hover:text-accent">View all →</Link>
              </div>
              <div className="grid grid-cols-3 gap-px bg-border mb-8">
                {[
                  { l: "New", v: stats.new_inquiries, color: "text-accent" },
                  { l: "Contacted", v: stats.contacted_inquiries, color: "text-foreground" },
                  { l: "Closed", v: stats.closed_inquiries, color: "text-foreground/55" },
                ].map((s) => (
                  <div key={s.l} className="bg-background p-5">
                    <div className="eyebrow text-foreground/55 mb-2">{s.l}</div>
                    <div className={`font-serif text-3xl ${s.color}`}>{s.v}</div>
                  </div>
                ))}
              </div>
              {stats.top_destinations?.length > 0 && (
                <div>
                  <div className="eyebrow mb-4">Top requested destinations</div>
                  <ul className="space-y-1">
                    {stats.top_destinations.map((d) => (
                      <li key={d.destination} className="flex items-center justify-between py-3 border-b border-border">
                        <span className="font-serif text-lg">{d.destination}</span>
                        <span className="eyebrow text-accent">{d.count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Content stats */}
            <div className="space-y-4">
              <div className="border border-border p-6">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin size={16} strokeWidth={1.25} className="text-accent" />
                  <div className="eyebrow">Destinations</div>
                </div>
                <div className="font-serif text-4xl mb-4">{stats.destinations || 0}</div>
                <Link to="/admin/destinations" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] hover:text-accent">
                  Manage <ArrowRight size={12} />
                </Link>
              </div>
              <div className="border border-border p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Package size={16} strokeWidth={1.25} className="text-accent" />
                  <div className="eyebrow">Packages</div>
                </div>
                <div className="font-serif text-4xl mb-4">{stats.packages || 0}</div>
                <Link to="/admin/packages" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] hover:text-accent">
                  Manage <ArrowRight size={12} />
                </Link>
              </div>
              <div className="border border-border p-6">
                <div className="flex items-center gap-3 mb-3">
                  <FileText size={16} strokeWidth={1.25} className="text-accent" />
                  <div className="eyebrow">Blog Posts</div>
                </div>
                <div className="font-serif text-4xl mb-4">{stats.blog_posts}</div>
                <Link to="/admin/blog" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] hover:text-accent">
                  Manage <ArrowRight size={12} />
                </Link>
              </div>
              <div className="border border-border p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Star size={16} strokeWidth={1.25} className="text-accent" />
                  <div className="eyebrow">Testimonials</div>
                </div>
                <div className="font-serif text-4xl mb-4">{stats.testimonials}</div>
                <Link to="/admin/testimonials" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] hover:text-accent">
                  Manage <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>

          {/* Recent inquiries */}
          {recent.length > 0 && (
            <div className="border border-border">
              <div className="px-8 py-5 border-b border-border flex items-center justify-between">
                <h3 className="font-serif text-2xl">Latest inquiries</h3>
                <Link to="/admin/inquiries" className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 hover:text-accent">View all →</Link>
              </div>
              <ul>
                {recent.map((inq) => (
                  <li key={inq.id} className="px-8 py-5 border-b border-border last:border-b-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="font-serif text-lg">{inq.full_name}</div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/55 mt-1">{inq.destination} · {inq.budget_range}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[11px] text-foreground/55">{new Date(inq.created_at).toLocaleDateString(undefined, {month: "short", day: "numeric"})}</span>
                      <span className={`px-3 py-1 border text-[10px] uppercase tracking-[0.22em] ${inq.status === "new" ? "border-accent text-accent" : inq.status === "contacted" ? "border-foreground/50 text-foreground/70" : "border-foreground/30 text-foreground/40"}`}>{inq.status}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
